import { memo, type ReactNode } from 'react'

import { TableCell, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { getCellValue } from './data-table.helper'
import type { DataTableAction, DataTableColumn } from './data-table.interface'
import { DataTableCheckbox } from './data-table-checkbox.atom'
import { DataTableRowActions } from './data-table-row-actions.molecule'

interface DataTableRowProps<TData> {
  row: TData
  rowId: string
  columnList: DataTableColumn<TData>[]
  isSelectable: boolean
  isSelected: boolean
  onSelectChange: (rowId: string, isChecked: boolean) => void
  actionList: DataTableAction<TData>[]
  hasActions: boolean
}

/**
 * Renders a single data table row with optional selection checkbox and actions.
 */
const DataTableRowInner = <TData,>({
  row,
  rowId,
  columnList,
  isSelectable,
  isSelected,
  onSelectChange,
  actionList,
  hasActions,
}: DataTableRowProps<TData>): ReactNode => (
  <TableRow data-state={isSelected ? 'selected' : undefined} className="data-table-row">
    {isSelectable && (
      <TableCell>
        <DataTableCheckbox
          isChecked={isSelected}
          onCheckedChange={(isChecked) => onSelectChange(rowId, isChecked)}
          ariaLabel={`Select row ${rowId}`}
        />
      </TableCell>
    )}
    {columnList.map((column) => (
      <TableCell key={column.id} className={cn(column.align === 'right' && 'text-right')}>
        {getCellValue(row, column)}
      </TableCell>
    ))}
    {hasActions && (
      <TableCell>
        <DataTableRowActions row={row} actionList={actionList} />
      </TableCell>
    )}
  </TableRow>
)

export const DataTableRow = memo(DataTableRowInner) as typeof DataTableRowInner
